import { useMemo } from "react";

import {
  computeCapabilities,
  type CampaignCapabilities,
  type CampaignRole,
  type UserCampaignRecord,
  type UserCampaignSummary,
} from "@west-marches/shared";

import { useEditorStore, type ViewAsRole } from "@/state/useEditorStore";

export interface EffectiveCampaignRole {
  /** Caller's real role on the campaign (owner | gm | player), or null if unknown. */
  actualRole: CampaignRole | null;
  /** Role the UI should treat the user as. Equals actualRole unless previewing. */
  effectiveRole: CampaignRole | null;
  /** Capabilities derived from effectiveRole (player capabilities while previewing). */
  effectiveCapabilities: CampaignCapabilities;
  /** Convenience: capabilities.canEditCampaign. */
  effectiveCanEdit: boolean;
  /** True when the caller is an admin (owner|gm) currently viewing as a player. */
  isPreviewing: boolean;
  /** True when the caller may toggle into preview (i.e., is owner|gm). */
  canToggleViewAs: boolean;
  /** Current viewAs setting from the editor store. */
  viewAs: ViewAsRole;
}

const NO_CAPABILITIES: CampaignCapabilities = computeCapabilities("player");

function isAdminRole(role: CampaignRole | null | undefined): boolean {
  return role === "owner" || role === "gm";
}

/**
 * Returns the role/capabilities the UI should use, accounting for the GM's
 * "view as player" preview toggle. Players are unaffected; admins downgrade to
 * the player capability set when previewing.
 */
export function useEffectiveCampaignRole(
  campaign: UserCampaignSummary | UserCampaignRecord | null | undefined,
): EffectiveCampaignRole {
  const viewAs = useEditorStore((s) => s.viewAs);
  const actualRole = campaign?.myRole ?? null;
  const canToggleViewAs = isAdminRole(actualRole);
  const isPreviewing = canToggleViewAs && viewAs === "player";

  return useMemo(() => {
    const effectiveRole: CampaignRole | null = isPreviewing ? "player" : actualRole;
    let effectiveCapabilities: CampaignCapabilities;
    if (isPreviewing) {
      effectiveCapabilities = computeCapabilities("player");
    } else if (campaign?.capabilities) {
      effectiveCapabilities = campaign.capabilities;
    } else if (actualRole) {
      effectiveCapabilities = computeCapabilities(actualRole);
    } else {
      effectiveCapabilities = NO_CAPABILITIES;
    }
    return {
      actualRole,
      effectiveRole,
      effectiveCapabilities,
      effectiveCanEdit: effectiveCapabilities.canEditCampaign,
      isPreviewing,
      canToggleViewAs,
      viewAs,
    };
  }, [actualRole, campaign?.capabilities, canToggleViewAs, isPreviewing, viewAs]);
}
